import React, { useState } from "react";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TableCell,
  useTheme,
  Theme,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Link, useNavigate } from "react-router-dom";
import { SortType } from "../../../models/SortType";
import {
  CustomMenu,
  CustomMenuItem,
  StyledTableCell,
  StyledTableRow,
} from "../../../styles/Shipmentstyles";
import { UserList } from "../../../models/User/User";
import AppTablePagenation from "../common/pages/AppTablePagenation";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import { useStores } from "../../../use-stores";
import AppTableHeaderCell from "../common/pages/AppTableHeaderCell";
import AppTableRowCell from "../common/pages/AppTableRowCell";
import { faArrowDown, faArrowUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatDate } from "../common/functions/formatDate";

interface UserTableProp {
  maxHeight: number;
  users: UserList[];
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy: string;
  onDelete: (custormerId: number) => void;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  sortType: SortType;
  onSortChange: (column: string, sortType: SortType) => void;
}

const sortHeaderLabelSx = {
  fontSize: "0.9rem",
  fontWeight: 600,
  color: "#93969B",
  "&.Mui-active": {
    color: "#93969B",
  },
} as const;

const UserTable = ({
  maxHeight,
  users,
  page_no,
  limit,
  total_count,
  sortBy,
  onPageChange,
  onRowsPerPageChange,
  onDelete,
  sortType,
  onSortChange,
}: UserTableProp) => {
  let navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedId] = useState<number[]>([]);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const { userStore } = useStores();
  const isOpen = Boolean(anchorEl);
  const theme = useTheme<Theme>();

  const stickyPaperBg = theme.palette.customColors.white;

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    currentUserId: number
  ) => {
    setAnchorEl(event.currentTarget);
    setCurrentUserId(currentUserId);
  };

  const handleDelete = () => {
    if (currentUserId !== null) {
      onDelete(currentUserId);
    }
    handleMenuClose();
  };

  const handleEdit = () => {
    if (currentUserId)
      navigate("/users/add-edit-user?user-id=" + currentUserId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentUserId(null);
  };

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? (
        <FontAwesomeIcon icon={faArrowUp} fontSize={"0.7rem"} color="#000000" />
      ) : (
        <FontAwesomeIcon
          icon={faArrowDown}
          fontSize={"0.7rem"}
          color="#000000"
        />
      );
    }

    return (
      <Box sx={{ display: "flex", gap: "2px" }}>
        <FontAwesomeIcon icon={faArrowUp} fontSize={"0.6rem"} />
        <FontAwesomeIcon icon={faArrowDown} fontSize={"0.6rem"} />
      </Box>
    );
  };

  const isViewOnly = (): boolean => {
    return (
      userStore.hasPermission(EmployeeRoles.VIEW_USER) &&
      !userStore.hasPermission(EmployeeRoles.EDIT_USER) &&
      !userStore.hasPermission(EmployeeRoles.CREATE_USER)
    );
  };

  const rowClickable = (): boolean => {
    return (
      userStore.hasPermission(EmployeeRoles.VIEW_USER) ||
      userStore.hasPermission(EmployeeRoles.EDIT_USER) ||
      userStore.hasPermission(EmployeeRoles.CREATE_USER)
    );
  };

  return (
    <>
      {selectedId?.length > 0 && (
        <Box sx={{ paddingLeft: 2, paddingTop: 1, fontSize: "0.85rem" }}>
          {`Selected - ` + selectedId?.length}
        </Box>
      )}

      <TableContainer
        sx={{ maxHeight: maxHeight, minHeight: window.innerHeight - 350 }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <StyledTableCell
                sx={{
                  textAlign: "center",
                  minWidth: "80px",
                  fontSize: "0.9rem",
                  color: "#93969B",
                  fontWeight: 600,
                  position: "sticky",
                  left: 0,
                  zIndex: 12,
                  backgroundColor: stickyPaperBg,
                }}
              >
                <TableSortLabel
                  active={sortBy === "id"}
                  direction={sortType === SortType.ASC ? "asc" : "desc"}
                  onClick={() =>
                    onSortChange(
                      "id",
                      sortType === SortType.DESC
                        ? SortType.ASC
                        : SortType.DESC
                    )
                  }
                  IconComponent={() => (
                    <span
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        marginLeft: "4px",
                      }}
                    >
                      {renderSortIcon("id")}
                    </span>
                  )}
                  sx={sortHeaderLabelSx}
                >
                  USER ID
                </TableSortLabel>
              </StyledTableCell>
              <AppTableHeaderCell title="COMPANY NAME" minWidth="200px" />
              <AppTableHeaderCell title="USER NAME" />
              <AppTableHeaderCell title="CUSTOMER ID" />
              <AppTableHeaderCell title="CUSTOMER NAME" />
              <AppTableHeaderCell title="LAST EDITED BY" />
              <AppTableHeaderCell title="EMAIL" />
              <AppTableHeaderCell title="ACTIVE" />
              <StyledTableCell
                sx={{
                  textAlign: "center",
                  minWidth: "180px",
                  fontSize: "0.9rem",
                  color: "#93969B",
                  fontWeight: 600,
                }}
              >
                <TableSortLabel
                  active={sortBy === "created_date"}
                  direction={sortType === SortType.ASC ? "asc" : "desc"}
                  onClick={() =>
                    onSortChange(
                      "created_date",
                      sortType === SortType.DESC
                        ? SortType.ASC
                        : SortType.DESC
                    )
                  }
                  IconComponent={() => (
                    <span
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        marginLeft: "4px",
                      }}
                    >
                      {renderSortIcon("created_date")}
                    </span>
                  )}
                  sx={sortHeaderLabelSx}
                >
                  CREATED ON
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell
                sx={{
                  textAlign: "center",
                  minWidth: "180px",
                  fontSize: "0.9rem",
                  color: "#93969B",
                  fontWeight: 600,
                }}
              >
                <TableSortLabel
                  active={sortBy === "modified_date"}
                  direction={sortType === SortType.ASC ? "asc" : "desc"}
                  onClick={() =>
                    onSortChange(
                      "modified_date",
                      sortType === SortType.DESC
                        ? SortType.ASC
                        : SortType.DESC
                    )
                  }
                  IconComponent={() => (
                    <span
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        marginLeft: "4px",
                      }}
                    >
                      {renderSortIcon("modified_date")}
                    </span>
                  )}
                  sx={sortHeaderLabelSx}
                >
                  LAST MODIFIED
                </TableSortLabel>
              </StyledTableCell>
              {(userStore.hasPermission(EmployeeRoles.CREATE_USER) ||
                userStore.hasPermission(EmployeeRoles.EDIT_USER)) && (
                <AppTableHeaderCell
                  sx={{
                    textAlign: "center",
                    fontSize: "0.9rem",
                    position: "sticky",
                    right: 0,
                    zIndex: 11,
                    backgroundColor: stickyPaperBg,
                  }}
                  title="ACTIONS"
                />
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user: UserList) => {
              const canNavigateRow =
                rowClickable() && user.active === 1;
              const idLinkStyle =
                canNavigateRow
                  ? {
                      cursor: "pointer" as const,
                      textDecoration: "underline",
                    }
                  : {};

              return (
                <StyledTableRow
                  key={user.id}
                  onClick={() => {
                    if (canNavigateRow) {
                      navigate(`/users/add-edit-user?user-id=${user.id}`);
                    }
                  }}
                  sx={{
                    cursor: isViewOnly() ? "default" : "pointer",
                  }}
                >
                  <AppTableRowCell
                    minWidth="80px"
                    sx={{
                      fontSize: "0.9rem",
                      position: "sticky",
                      left: 0,
                      zIndex: 10,
                      backgroundColor: stickyPaperBg,
                      ...idLinkStyle,
                    }}
                  >
                    {user.id}
                  </AppTableRowCell>
                  <AppTableRowCell sx={{ fontSize: "0.9rem" }}>
                    {user.company_name || "-"}
                  </AppTableRowCell>
                  <AppTableRowCell sx={{ fontSize: "0.9rem" }}>{`${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "-"}</AppTableRowCell>
                  <AppTableRowCell sx={{ fontSize: "0.9rem" }}>
                    {user.customer_id}
                  </AppTableRowCell>
                  <AppTableRowCell sx={{ fontSize: "0.9rem" }}>
                    {user.customer_name}
                  </AppTableRowCell>
                  <AppTableRowCell sx={{ fontSize: "0.9rem" }}>
                    {user.added_by_name}
                  </AppTableRowCell>
                  <AppTableRowCell sx={{ fontSize: "0.9rem" }}>
                    {user.email}
                  </AppTableRowCell>
                  <AppTableRowCell sx={{ fontSize: "0.9rem" }}>
                    {user.active === 0 ? "No" : "Yes"}
                  </AppTableRowCell>
                  <AppTableRowCell sx={{ fontSize: "0.9rem" }}>
                    {user.created_date ? formatDate(user.created_date) : "-"}
                  </AppTableRowCell>
                  <AppTableRowCell sx={{ fontSize: "0.9rem" }}>
                    {user.modified_date
                      ? formatDate(user.modified_date)
                      : "-"}
                  </AppTableRowCell>
                  {(userStore.hasPermission(EmployeeRoles.CREATE_USER) ||
                    userStore.hasPermission(EmployeeRoles.EDIT_USER)) &&
                    user.active === 1 && (
                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: "0.9rem",
                          width: "10%",
                          position: "sticky",
                          right: 0,
                          zIndex: 8,
                          backgroundColor: stickyPaperBg,
                          borderBottom: "1px solid #E2E2E2",
                        }}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <IconButton
                          onClick={(event) => {
                            event.stopPropagation();
                            handleMenuClick(event, Number(user.id));
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                        <CustomMenu
                          anchorEl={anchorEl}
                          open={isOpen}
                          onClose={handleMenuClose}
                          anchorOrigin={{
                            vertical: "top",
                            horizontal: "right",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                          }}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <CustomMenuItem onClick={() => handleEdit()}>
                            <Link
                              to={`/users/add-edit-user?user-id=${currentUserId}`}
                              style={{
                                textDecoration: "none",
                                color: "black",
                              }}
                            >
                              View/Edit
                            </Link>
                          </CustomMenuItem>
                          {!!user.active && (
                            <CustomMenuItem onClick={handleDelete}>
                              Delete
                            </CustomMenuItem>
                          )}
                        </CustomMenu>
                      </TableCell>
                    )}
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <AppTablePagenation
        fontSize="0.8rem"
        pageNo={page_no}
        limit={limit}
        totalCount={total_count}
        onPageChange={onPageChange}
        handleRowsChange={handleRowsChange}
      />
    </>
  );
};

export default UserTable;
